/**
 * ChartTooltip - Unified tooltip styling for analysis charts.
 *
 * Provides consistent styling across all chart tooltips using Mantine Paper.
 * Supports measurements, dosing, and custom content.
 */
import { Paper, Text, Group, Box, Stack } from '@mantine/core';
import { format } from 'date-fns';

/** Standard tooltip date format: "Jan 15, 2024 at 2:30 PM" */
export const TOOLTIP_DATE_FORMAT = "MMM d, yyyy 'at' h:mm a";

/** Standard tooltip date-only format: "Jan 15, 2024" */
export const TOOLTIP_DATE_ONLY_FORMAT = 'MMM d, yyyy';

/**
 * Base tooltip container with consistent Mantine styling.
 * Use this to wrap tooltip content for unified appearance.
 */
export function TooltipContainer({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Paper
      p="xs"
      radius="sm"
      withBorder
      style={{
        backgroundColor: 'var(--mantine-color-body)',
        maxWidth: 320,
      }}
    >
      {children}
    </Paper>
  );
}

/**
 * Tooltip header showing formatted date/time.
 */
export function TooltipHeader({
  timestamp,
  showTime = true,
}: {
  timestamp: number | string | Date;
  showTime?: boolean;
}): JSX.Element {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  const formatString = showTime ? TOOLTIP_DATE_FORMAT : TOOLTIP_DATE_ONLY_FORMAT;

  return (
    <Text size="xs" fw={600} c="dimmed" mb={4}>
      {format(date, formatString)}
    </Text>
  );
}

/**
 * A single row in the tooltip showing a colored indicator, label, and value.
 */
export function TooltipRow({
  color,
  label,
  value,
  unit,
  subtitle,
}: {
  color: string;
  label: string;
  value: string | number;
  unit?: string;
  subtitle?: string;
}): JSX.Element {
  return (
    <Group gap={8} wrap="nowrap" align="flex-start">
      <Box
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          backgroundColor: color,
          flexShrink: 0,
          marginTop: 3,
        }}
      />
      <Stack gap={0}>
        <Text size="sm" lh={1.3}>
          <Text span fw={500}>{label}:</Text>{' '}
          <Text span>
            {value}
            {unit && ` ${unit}`}
          </Text>
        </Text>
        {subtitle && (
          <Text size="xs" c="dimmed" lh={1.2}>
            {subtitle}
          </Text>
        )}
      </Stack>
    </Group>
  );
}

/**
 * Display a measurement note in a tooltip (italicized, dimmed).
 */
export function TooltipNote({
  note,
}: {
  note: string;
}): JSX.Element {
  return (
    <Text size="xs" c="dimmed" fs="italic" mt={2} style={{ maxWidth: 250 }}>
      {note}
    </Text>
  );
}

/**
 * Footer row for totals or additional info (with top border separator).
 */
export function TooltipFooter({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      mt={6}
      pt={6}
      style={{
        borderTop: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      <Text size="sm" fw={500}>
        {children}
      </Text>
    </Box>
  );
}

/**
 * Format a measurement value based on parameter type.
 */
export function formatMeasurementValue(value: number, paramKey: string): string {
  if (paramKey === 'po4') {
    return value.toFixed(3);
  } else if (['ca', 'mg', 'k'].includes(paramKey)) {
    return Math.round(value).toString();
  } else {
    return value.toFixed(2);
  }
}

/**
 * Get display name for a device/source.
 */
export function getDeviceDisplayName(device: string): string {
  const deviceNames: Record<string, string> = {
    mastertronic: 'Mastertronic',
    focustronic: 'Focustronic',
    dosetronic: 'Dosetronic',
    aquawiz: 'AquaWiz KH1',
    apex: 'Apex Fusion',
    aquarimate: 'Aquarimate',
    manual: 'Manual Entry',
  };
  return deviceNames[device.toLowerCase()] || device;
}
